// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import { createAsyncThunk } from "@reduxjs/toolkit";

import axiosInstance from "../../services/axiosInstance";
import { apiCall } from "../utils/utils";

// get User
export const fetchUser = createAsyncThunk(
  "user/fetchUser",
  async (_, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get("user/me/"), rejectWithValue),
);

// get user invites
export const fetchUserInvites = createAsyncThunk(
  "user/fetchUserInvites",
  async (_, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get("user/member/invites/"), rejectWithValue),
);

// accept company member invitation
export const acceptCompanyInvitation = createAsyncThunk(
  "company/acceptCompanyInvitation",
  async (data, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.post(`company/member/accept-invite/`, data),
      rejectWithValue,
    ),
);

// decline company member invitation
export const declineCompanyInvitation = createAsyncThunk(
  "company/declineCompanyInvitation",
  async (data, { rejectWithValue }) => {
    try {
      const res = apiCall(
        () => axiosInstance.post(`company/member/decline-invite/`, data),
        rejectWithValue,
      );
      // if response has payload, it's an error, return it
      if (
        res &&
        typeof res === "object" &&
        Object.prototype.hasOwnProperty.call(res, "payload")
      ) {
        return res;
      }

      // success (204 -> res is null), return data for reducer to update state
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error revoking invitation");
    }
  },
);

// get all users / superUser permission only
export const fetchUsersAll = createAsyncThunk(
  "user/fetchUsersAll",
  async (_, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get("user/all/"), rejectWithValue),
);

// get users user/filtered/
export const fetchCompanyUsers = createAsyncThunk(
  "user/fetchCompanyUsers",
  async (
    {
      companyId,
      companyRole = null,
      companyRoleLevel = "Single level",
      disabled = null,
    },
    { rejectWithValue },
  ) => {
    const updatedData = {
      company_uuid: companyId,
      company_role: companyRole,
      company_role_level: companyRoleLevel,
      include_disabled: disabled,
    };
    return apiCall(
      () => axiosInstance.post("user/filtered/", updatedData),
      rejectWithValue,
    );
  },
);

export const getLocalUsers = createAsyncThunk(
  "user/getLocalUsers",
  async (state, { rejectWithValue }) =>
    // possible states: all, live, UUID
    apiCall(
      () => axiosInstance.get(`local-account/${state}/`),
      rejectWithValue,
    ),
);

export const createLocalUser = createAsyncThunk(
  "user/createLocalUser",
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post("local-account/", data), rejectWithValue),
);

export const updateLocalUser = createAsyncThunk(
  "user/updateLocalUser",
  async (data, { rejectWithValue }) => {
    try {
      const res = await apiCall(
        () => axiosInstance.patch(`local-account/`, data),
        rejectWithValue,
      );
      // if response has payload, it's an error, return it
      if (
        res &&
        typeof res === "object" &&
        Object.prototype.hasOwnProperty.call(res, "payload")
      ) {
        return res;
      }

      // success (204 -> res is null), return data for reducer to update state
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error updating user");
    }
  },
);

export const resetLocalUserPassword = createAsyncThunk(
  "user/resetLocalUserPassword",
  async (data, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.post(`local-account/reset-password/`, data),
      rejectWithValue,
    ),
);

export const deactivateLocalUser = createAsyncThunk(
  "user/deactivateLocalUser",
  async (username, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.delete(`local-account/${username}/`),
      rejectWithValue,
    ),
);

export const activateLocalUser = createAsyncThunk(
  "user/activateLocalUser",
  async (data, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.post(`local-account/activate/`, data),
      rejectWithValue,
    ),
);

export const logoutLocalUser = createAsyncThunk(
  "user/logoutLocalUser",
  async (data, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.post("local-account/logout-all-sessions/", data),
      rejectWithValue,
    ),
);
